/**
 * Auxiliary Ethers Provider for secondary chain connections
 * This is a fully independent stack from the primary EthersProvider,
 * allowing users to connect to different chains simultaneously (e.g., for bridging)
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useMemo } from 'react';
import { BrowserProvider, JsonRpcSigner } from 'ethers';
import { EIP1193Provider } from '@/hooks/useWalletDiscovery';
import { ChainConfig, getAddChainParams, chainIdToHex, BRIDGE_CHAINS } from '@/lib/chains';
import { isPageVisible } from '@/lib/pageVisibility';
import { formatCompactBNB } from '@/lib/ethersUtils';

// Storage key for persisted aux chain preference
const AUX_CHAIN_STORAGE_KEY = 'welephant_aux_chain_id';

interface AuxEthersContextValue {
  // Provider state
  provider: BrowserProvider | null;
  signer: JsonRpcSigner | null;

  // Account state
  address: string | undefined;
  isConnected: boolean;
  balance: bigint | undefined;
  formattedBalance: string;

  // Chain state
  chainId: number | undefined;
  chainConfig: ChainConfig | undefined;
  targetChain: ChainConfig | undefined;

  // Connection actions
  connect: (chain: ChainConfig, selectedProvider?: EIP1193Provider) => Promise<void>;
  disconnect: () => void;
  switchChain: (chain: ChainConfig) => Promise<void>;
  setTargetChain: (chain: ChainConfig) => void;

  // Status
  isConnecting: boolean;
  isSwitchingChain: boolean;
  error: string | null;
}

const AuxEthersContext = createContext<AuxEthersContextValue | null>(null);

interface AuxEthersProviderProps {
  children: ReactNode;
}

export function AuxEthersProvider({ children }: AuxEthersProviderProps) {
  const [provider, setProvider] = useState<BrowserProvider | null>(null);
  const [signer, setSigner] = useState<JsonRpcSigner | null>(null);
  const [address, setAddress] = useState<string | undefined>(undefined);
  const [balance, setBalance] = useState<bigint | undefined>(undefined);
  const [chainId, setChainId] = useState<number | undefined>(undefined);
  const [targetChain, setTargetChain] = useState<ChainConfig | undefined>(undefined);

  const [isConnecting, setIsConnecting] = useState(false);
  const [isSwitchingChain, setIsSwitchingChain] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Store the current wallet provider reference
  const [walletProvider, setWalletProvider] = useState<EIP1193Provider | null>(null);

  // Get chain config for current chainId
  const chainConfig = useMemo(() => {
    if (!chainId) return undefined;
    return BRIDGE_CHAINS.find(c => c.id === chainId);
  }, [chainId]);

  // Load persisted chain preference
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = localStorage.getItem(AUX_CHAIN_STORAGE_KEY);
    if (stored) {
      const storedChainId = parseInt(stored, 10);
      const chain = BRIDGE_CHAINS.find(c => c.id === storedChainId);
      if (chain) {
        setTargetChain(chain);
      }
    }
  }, []);

  // Persist chain preference
  useEffect(() => {
    if (typeof window === 'undefined' || !targetChain) return;
    localStorage.setItem(AUX_CHAIN_STORAGE_KEY, targetChain.id.toString());
  }, [targetChain]);

  // Fetch balance periodically when connected
  useEffect(() => {
    if (!provider || !address) return;

    const fetchBalance = async () => {
      if (!isPageVisible()) return;
      try {
        const bal = await provider.getBalance(address);
        setBalance(bal);
      } catch (err) {
        console.error('[AuxEthersProvider] Balance fetch error:', err);
      }
    };

    fetchBalance();
    const interval = setInterval(fetchBalance, 10000);
    return () => clearInterval(interval);
  }, [provider, address]);

  // Listen for account and chain changes on the wallet provider
  useEffect(() => {
    if (!walletProvider) return;

    const handleAccountsChanged = async (...args: unknown[]) => {
      const accounts = args[0] as string[];
      if (!accounts || accounts.length === 0) {
        // Disconnected
        setSigner(null);
        setAddress(undefined);
        setBalance(undefined);
      } else if (provider) {
        try {
          const newSigner = await provider.getSigner();
          setSigner(newSigner);
          setAddress(accounts[0]);
        } catch (err) {
          console.error('[AuxEthersProvider] Failed to get new signer:', err);
        }
      }
    };

    const handleChainChanged = async (chainIdHex: unknown) => {
      const newChainId = parseInt(chainIdHex as string, 16);
      setChainId(newChainId);

      // Recreate provider with new chain
      if (walletProvider) {
        try {
          const browserProvider = new BrowserProvider(walletProvider);
          setProvider(browserProvider);

          const accounts = await browserProvider.listAccounts();
          if (accounts.length > 0) {
            const newSigner = await browserProvider.getSigner();
            setSigner(newSigner);
            setAddress(await newSigner.getAddress());
          }
        } catch (err) {
          console.error('[AuxEthersProvider] Failed to recreate provider after chain change:', err);
        }
      }
    };

    walletProvider.on?.('accountsChanged', handleAccountsChanged);
    walletProvider.on?.('chainChanged', handleChainChanged);

    return () => {
      walletProvider.removeListener?.('accountsChanged', handleAccountsChanged);
      walletProvider.removeListener?.('chainChanged', handleChainChanged);
    };
  }, [walletProvider, provider]);

  // Connect to a specific chain
  const connect = useCallback(async (chain: ChainConfig, selectedProvider?: EIP1193Provider) => {
    const wallet = selectedProvider || window.ethereum;

    if (!wallet) {
      setError('No wallet detected. Please install a Web3 wallet.');
      throw new Error('No wallet detected');
    }

    setIsConnecting(true);
    setError(null);

    try {
      // Request accounts
      let browserProvider = new BrowserProvider(wallet);
      await browserProvider.send('eth_requestAccounts', []);

      // Check current chain
      const network = await browserProvider.getNetwork();
      const currentChainId = Number(network.chainId);

      // Switch to target chain if needed
      if (currentChainId !== chain.id) {
        try {
          await wallet.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: chainIdToHex(chain.id) }],
          });
        } catch (switchError: unknown) {
          const err = switchError as { code?: number };
          if (err.code === 4902) {
            // Chain not added, add it
            await wallet.request({
              method: 'wallet_addEthereumChain',
              params: [getAddChainParams(chain)],
            });
          } else {
            throw switchError;
          }
        }

        // Recreate provider after chain switch
        browserProvider = new BrowserProvider(wallet);
      }

      const newSigner = await browserProvider.getSigner();
      const newAddress = await newSigner.getAddress();
      const finalNetwork = await browserProvider.getNetwork();

      setProvider(browserProvider);
      setSigner(newSigner);
      setAddress(newAddress);
      setChainId(Number(finalNetwork.chainId));
      setTargetChain(chain);
      setWalletProvider(wallet);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to connect';
      setError(message);
      console.error('[AuxEthersProvider] Connect failed:', err);
      throw err;
    } finally {
      setIsConnecting(false);
    }
  }, []);

  // Switch to a different chain
  const switchChain = useCallback(async (chain: ChainConfig) => {
    if (!walletProvider) {
      setError('Not connected to a wallet');
      throw new Error('Not connected');
    }

    setIsSwitchingChain(true);
    setError(null);

    try {
      try {
        await walletProvider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: chainIdToHex(chain.id) }],
        });
      } catch (switchError: unknown) {
        const err = switchError as { code?: number };
        if (err.code === 4902) {
          await walletProvider.request({
            method: 'wallet_addEthereumChain',
            params: [getAddChainParams(chain)],
          });
        } else {
          throw switchError;
        }
      }

      // Provider will be updated via chainChanged event
      setTargetChain(chain);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to switch chain';
      setError(message);
      console.error('[AuxEthersProvider] Switch chain failed:', err);
      throw err;
    } finally {
      setIsSwitchingChain(false);
    }
  }, [walletProvider]);

  // Disconnect
  const disconnect = useCallback(() => {
    setProvider(null);
    setSigner(null);
    setAddress(undefined);
    setBalance(undefined);
    setChainId(undefined);
    setWalletProvider(null);
    setError(null);
  }, []);

  // Set target chain without connecting
  const handleSetTargetChain = useCallback((chain: ChainConfig) => {
    setTargetChain(chain);
  }, []);

  const value = useMemo<AuxEthersContextValue>(() => ({
    provider,
    signer,
    address,
    isConnected: !!address && !!signer,
    balance,
    formattedBalance: balance !== undefined
      ? formatCompactBNB(balance)
      : '0',
    chainId,
    chainConfig,
    targetChain,
    connect,
    disconnect,
    switchChain,
    setTargetChain: handleSetTargetChain,
    isConnecting,
    isSwitchingChain,
    error,
  }), [
    provider,
    signer,
    address,
    balance,
    chainId,
    chainConfig,
    targetChain,
    connect,
    disconnect,
    switchChain,
    handleSetTargetChain,
    isConnecting,
    isSwitchingChain,
    error
  ]);

  return (
    <AuxEthersContext.Provider value={value}>
      {children}
    </AuxEthersContext.Provider>
  );
}

export function useAuxEthersContext(): AuxEthersContextValue {
  const context = useContext(AuxEthersContext);
  if (!context) {
    throw new Error('useAuxEthersContext must be used within AuxEthersProvider');
  }
  return context;
}
